/**
 * System config mutations using React Relay.
 *
 * Provides the `patchSystemConfig` GraphQL mutation (beta API), which
 * shallow-merges the given top-level keys into System.config. Keys absent from
 * the patch are left untouched, so callers send only what they changed.
 *
 * The project-wide `Json` scalar decodes *input* from a JSON-encoded string
 * (AshGraphql.Types.JSON only accepts a string literal), while it returns
 * *output* already parsed. The patch is therefore stringified here, in one
 * place, and `result.config` comes back as a plain object.
 *
 * Note: Run `npm run relay` after making changes to GraphQL mutations
 * to regenerate the type artifacts.
 */

import { useCallback, useState } from 'react';
import { commitMutation } from 'react-relay';
import graphql from 'babel-plugin-relay/macro';
import { relayEnvironment } from '../../../../relay';
import type { useSystemConfigMutationsPatchMutation } from './__generated__/useSystemConfigMutationsPatchMutation.graphql';

// ============================================================================
// GraphQL Mutations
// ============================================================================

const PatchSystemConfigMutation = graphql`
  mutation useSystemConfigMutationsPatchMutation(
    $id: ID!
    $input: PatchSystemConfigInput!
  ) {
    patchSystemConfig(id: $id, input: $input) {
      errors {
        code
        fields
        message
        shortMessage
      }
      result {
        id
        config
      }
    }
  }
`;

// ============================================================================
// Types
// ============================================================================

export interface MutationError {
  code?: string | null;
  fields?: readonly string[] | null;
  message?: string | null;
  shortMessage?: string | null;
}

export interface MutationResult<T> {
  data: T | null;
  errors: MutationError[];
  isSuccess: boolean;
}

/** Top-level config keys to add or overwrite. A null value stores null. */
export type SystemConfigPatch = Record<string, unknown>;

export type PatchSystemConfigData =
  useSystemConfigMutationsPatchMutation['response']['patchSystemConfig']['result'];

// ============================================================================
// Imperative API (without hooks)
// ============================================================================

/**
 * Patch a system's config imperatively (without a hook).
 *
 * @param systemId the system's global id (gid)
 * @param patch top-level keys to merge into System.config
 */
export function patchSystemConfigMutation(
  systemId: string,
  patch: SystemConfigPatch
): Promise<MutationResult<PatchSystemConfigData>> {
  return new Promise((resolve) => {
    commitMutation<useSystemConfigMutationsPatchMutation>(relayEnvironment, {
      mutation: PatchSystemConfigMutation,
      variables: { id: systemId, input: { patch: JSON.stringify(patch) } },
      onCompleted: (response: useSystemConfigMutationsPatchMutation['response']) => {
        const payload = response.patchSystemConfig;
        resolve({
          data: payload.result ?? null,
          errors: payload.errors as MutationError[],
          isSuccess: payload.errors.length === 0 && payload.result !== null,
        });
      },
      onError: (error: Error) => {
        resolve({
          data: null,
          errors: [{ message: error.message }],
          isSuccess: false,
        });
      },
    });
  });
}

// ============================================================================
// Hooks
// ============================================================================

/**
 * Hook for patching a system's config.
 *
 * @example
 * const { patchSystemConfig, isLoading } = usePatchSystemConfigMutation();
 *
 * const result = await patchSystemConfig(system.gid, { contracted_assets: 12 });
 * if (result.isSuccess) {
 *   toast.success('Fleet settings updated');
 * }
 */
export function usePatchSystemConfigMutation() {
  const [isLoading, setIsLoading] = useState(false);

  const patchSystemConfig = useCallback(
    async (
      systemId: string,
      patch: SystemConfigPatch
    ): Promise<MutationResult<PatchSystemConfigData>> => {
      setIsLoading(true);
      try {
        return await patchSystemConfigMutation(systemId, patch);
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  return { patchSystemConfig, isLoading };
}
